import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { recebimentoService, despesaService, clienteService, tipoDespesaService } from '../../services/api';

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`raiox-tabpanel-${index}`}
      aria-labelledby={`raiox-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Recebimento {
  id: number;
  descricao: string;
  valor: number;
  data_recebimento: string;
  data_vencimento: string;
  status: string;
  id_cliente: number;
  cliente_nome: string;
  forma_pagamento: string;
}

interface Despesa {
  id: number;
  descricao: string;
  valor: number;
  data_despesa?: string;
  data_pagamento?: string;
  data_vencimento?: string;
  data_cadastro?: string;
  id_tipo_despesa: number;
  tipo_despesa_nome?: string;
  ativo: boolean;
  status: string;
  forma_pagamento?: string;
  observacoes?: string;
  id_usuario: number;
}

interface RaioXData {
  data: string;
  qtd_recebimentos: number;
  valor_recebimentos: number;
  qtd_despesas: number;
  valor_despesas: number;
  saldo: number;
}

const RaioXFinanceiro: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Função para obter primeiro e último dia do mês atual
  const getPrimeiroDiaDoMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  };

  const getUltimoDiaDoMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  // Estados para Recebimentos
  const [recebimentos, setRecebimentos] = useState<Recebimento[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [filtroRecebimentos, setFiltroRecebimentos] = useState({
    cliente: '',
    status: '',
    dataInicio: getPrimeiroDiaDoMes(),
    dataFim: getUltimoDiaDoMes()
  });

  // Opções de status como na tela Recebimentos.tsx
  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: 'warning' as const },
    { value: 'pago', label: 'Pago', color: 'success' as const },
    { value: 'vencido', label: 'Vencido', color: 'error' as const },
    { value: 'cancelado', label: 'Cancelado', color: 'default' as const }
  ];

  // Estados para Despesas
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [tiposDespesa, setTiposDespesa] = useState<any[]>([]);
  const [filtroDespesas, setFiltroDespesas] = useState({
    dataInicio: getPrimeiroDiaDoMes(),
    dataFim: getUltimoDiaDoMes(),
    tipo: ''
  });

  // Estados para Raio X
  const [raioxData, setRaioxData] = useState<RaioXData[]>([]);
  const [filtroRaioX, setFiltroRaioX] = useState({
    dataInicio: getPrimeiroDiaDoMes(),
    dataFim: getUltimoDiaDoMes()
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Carregar Clientes
  const carregarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/clientes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const clientesAtivos = data.data.filter((cliente: any) => 
          cliente.ativo
        );
        setClientes(clientesAtivos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  // Carregar Tipos de Despesa
  const carregarTiposDespesa = async () => {
    try {
      if (!user?.id) return;

      const response = await tipoDespesaService.listarPorUsuario(user?.id);
      
      let tipos = [];
      if (response && Array.isArray(response)) {
        tipos = response;
      } else if (response?.data && Array.isArray(response.data)) {
        tipos = response.data;
      }
      
      setTiposDespesa(tipos);
      console.log('✅ Tipos carregados:', tipos.length);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
      setTiposDespesa([]);
    }
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltroRecebimentos({
      cliente: '',
      status: '',
      dataInicio: getPrimeiroDiaDoMes(),
      dataFim: getUltimoDiaDoMes()
    });
  };

  // Carregar Recebimentos
  const carregarRecebimentos = async () => {
    try {
      setLoading(true);
      const response = await recebimentoService.buscarPorUsuario(user?.id);
      let dados = response.data?.data || [];

      // Aplicar filtros
      if (filtroRecebimentos.cliente) {
        dados = dados.filter((r: Recebimento) => r.cliente_nome === filtroRecebimentos.cliente);
      }
      if (filtroRecebimentos.status) {
        dados = dados.filter((r: Recebimento) => r.status === filtroRecebimentos.status);
      }
      if (filtroRecebimentos.dataInicio) {
        dados = dados.filter((r: Recebimento) => {
          const dataVencimento = r.data_vencimento.split('T')[0];
          return dataVencimento >= filtroRecebimentos.dataInicio;
        });
      }
      if (filtroRecebimentos.dataFim) {
        dados = dados.filter((r: Recebimento) => {
          const dataVencimento = r.data_vencimento.split('T')[0];
          return dataVencimento <= filtroRecebimentos.dataFim;
        });
      }

      setRecebimentos(dados);
    } catch (error) {
      console.error('Erro ao carregar recebimentos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar Despesas
  const carregarDespesas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/despesas/usuario/${user?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        let despesasData = data.data || [];
        
        // Aplicar filtros
        if (filtroDespesas.dataInicio) {
          despesasData = despesasData.filter((d: Despesa) => {
            const dataDespesa = (d.data_pagamento || d.data_vencimento || d.data_cadastro)?.split('T')[0];
            return dataDespesa ? dataDespesa >= filtroDespesas.dataInicio : false;
          });
        }
        if (filtroDespesas.dataFim) {
          despesasData = despesasData.filter((d: Despesa) => {
            const dataDespesa = (d.data_pagamento || d.data_vencimento || d.data_cadastro)?.split('T')[0];
            return dataDespesa ? dataDespesa <= filtroDespesas.dataFim : false;
          });
        }
        if (filtroDespesas.tipo) {
          console.log('🔍 Filtro tipo selecionado:', filtroDespesas.tipo, typeof filtroDespesas.tipo);
          despesasData = despesasData.filter((d: Despesa) => {
            console.log(`🔍 Despesa ${d.id}: id_tipo_despesa=${d.id_tipo_despesa} (${typeof d.id_tipo_despesa}) vs filtro=${filtroDespesas.tipo} (${typeof filtroDespesas.tipo})`);
            return d.id_tipo_despesa == parseInt(filtroDespesas.tipo);
          });
          console.log('✅ Despesas após filtro por tipo:', despesasData.length);
        }

        // Ordenar por data (mais recente primeiro)
        despesasData.sort((a: Despesa, b: Despesa) => {
          const dataA = new Date(a.data_pagamento || a.data_vencimento || a.data_cadastro || '');
          const dataB = new Date(b.data_pagamento || b.data_vencimento || b.data_cadastro || '');
          return dataB.getTime() - dataA.getTime();
        });

        setDespesas(despesasData);
      }
    } catch (error) {
      console.error('Erro ao carregar despesas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para limpar filtros da aba Despesas
  const limparFiltrosDespesas = async () => {
    const novoFiltro = {
      dataInicio: getPrimeiroDiaDoMes(),
      dataFim: getUltimoDiaDoMes(),
      tipo: ''
    };
    setFiltroDespesas(novoFiltro);
    
    // Atualizar lista automaticamente
    setTimeout(() => {
      carregarDespesas();
    }, 100);
  };

  // Carregar dados do Raio X
  const carregarRaioX = async () => {
    try {
      setLoading(true);
      
      // Buscar recebimentos e despesas
      const [recebimentosRes, despesasRes] = await Promise.all([
        recebimentoService.buscarPorUsuario(user?.id),
        despesaService.buscarPorUsuario(user?.id)
      ]);

      const recebimentosData = recebimentosRes.data?.data || [];
      const despesasData = despesasRes.data?.data || [];

      // Aplicar filtro de período
      let recebimentosFiltrados = recebimentosData;
      let despesasFiltradas = despesasData;

      if (filtroRaioX.dataInicio) {
        recebimentosFiltrados = recebimentosFiltrados.filter((r: Recebimento) => 
          r.data_vencimento >= filtroRaioX.dataInicio
        );
        despesasFiltradas = despesasFiltradas.filter((d: Despesa) => {
          const dataDespesa = d.data_vencimento;
          return dataDespesa ? dataDespesa >= filtroRaioX.dataInicio : false;
        });
      }

      if (filtroRaioX.dataFim) {
        recebimentosFiltrados = recebimentosFiltrados.filter((r: Recebimento) => 
          r.data_vencimento <= filtroRaioX.dataFim
        );
        despesasFiltradas = despesasFiltradas.filter((d: Despesa) => {
          const dataDespesa = d.data_vencimento;
          return dataDespesa ? dataDespesa <= filtroRaioX.dataFim : false;
        });
      }

      // Agrupar por data
      const dadosAgrupados: { [key: string]: RaioXData } = {};

      // Processar recebimentos
      recebimentosFiltrados.forEach((r: Recebimento) => {
        if (r.data_vencimento) {
          const data = r.data_vencimento.split('T')[0]; // Pegar apenas a data
          if (!dadosAgrupados[data]) {
            dadosAgrupados[data] = {
              data,
              qtd_recebimentos: 0,
              valor_recebimentos: 0,
              qtd_despesas: 0,
              valor_despesas: 0,
              saldo: 0
            };
          }
          dadosAgrupados[data].qtd_recebimentos++;
          dadosAgrupados[data].valor_recebimentos += parseFloat(r.valor?.toString()) || 0;
        }
      });

      // Processar despesas
      despesasFiltradas.forEach((d: Despesa) => {
        if (d.data_vencimento) {
          const data = d.data_vencimento.split('T')[0]; // Pegar apenas a data
          if (!dadosAgrupados[data]) {
            dadosAgrupados[data] = {
              data,
              qtd_recebimentos: 0,
              valor_recebimentos: 0,
              qtd_despesas: 0,
              valor_despesas: 0,
              saldo: 0
            };
          }
          dadosAgrupados[data].qtd_despesas++;
          dadosAgrupados[data].valor_despesas += parseFloat(d.valor?.toString()) || 0;
        }
      });

      // Converter para array e ordenar por data
      const dadosArray = Object.values(dadosAgrupados).map(item => ({
        ...item,
        saldo: item.valor_recebimentos - item.valor_despesas
      })).sort((a: any, b: any) => 
        new Date(b.data).getTime() - new Date(a.data).getTime()
      );

      setRaioxData(dadosArray);
    } catch (error) {
      console.error('Erro ao carregar dados do Raio X:', error);
    } finally {
      setLoading(false);
    }
  };

  // Efeitos para carregar dados quando a aba muda
  useEffect(() => {
    if (user?.id) {
      carregarClientes();
      carregarTiposDespesa();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      if (tabValue === 0) {
        carregarRecebimentos();
      } else if (tabValue === 1) {
        carregarDespesas();
      } else if (tabValue === 2) {
        carregarRaioX();
      }
    }
  }, [tabValue, user?.id]);

  // Função para formatar moeda (corrigir NaN)
  const formatarMoeda = (valor: number | string | null | undefined): string => {
    const numero = parseFloat(valor?.toString() || '0') || 0;
    return numero.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatarData = (data: string) => {
    if (!data || data === '' || data === 'null' || data === 'undefined') {
      return '-';
    }
    
    try {
      // Tentar diferentes formatos de data
      let dataObj;
      
      // Se já contém 'T', usar diretamente
      if (data.includes('T')) {
        dataObj = new Date(data);
      } 
      // Se está no formato YYYY-MM-DD, adicionar T00:00:00
      else if (data.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dataObj = new Date(data + 'T00:00:00');
      }
      // Se está no formato DD/MM/YYYY, converter
      else if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [dia, mes, ano] = data.split('/');
        dataObj = new Date(`${ano}-${mes}-${dia}T00:00:00`);
      }
      // Tentar criar diretamente
      else {
        dataObj = new Date(data);
      }
      
      // Verificar se a data é válida
      if (isNaN(dataObj.getTime())) {
        return '-';
      }
      
      return dataObj.toLocaleDateString('pt-BR');
    } catch (error) {
      console.warn('Erro ao formatar data:', data, error);
      return '-';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0, mb: 0 }}>
          <AnalyticsIcon />
          RaioX Financeiro
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mt: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Recebimentos" />
          <Tab label="Despesas" />
          <Tab label="Raio X" />
        </Tabs>

        {/* Aba Recebimentos */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth variant="outlined" sx={{ height: '46px' }}>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filtroRecebimentos.cliente}
                  onChange={(e) => setFiltroRecebimentos(prev => ({ ...prev, cliente: e.target.value }))}
                  label="Cliente"
                  sx={{ height: '46px' }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.nome}>{cliente.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" sx={{ height: '46px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtroRecebimentos.status}
                  onChange={(e) => setFiltroRecebimentos(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                  sx={{ height: '46px' }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Vencimento Inicial"
                type="date"
                value={filtroRecebimentos.dataInicio}
                onChange={(e) => setFiltroRecebimentos(prev => ({ ...prev, dataInicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Vencimento Final"
                type="date"
                value={filtroRecebimentos.dataFim}
                onChange={(e) => setFiltroRecebimentos(prev => ({ ...prev, dataFim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={1.75}>
              <Button
                fullWidth
                variant="contained"
                onClick={carregarRecebimentos}
                sx={{ height: '46px' }}
                disabled={loading}
              >
                Atualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={1.75}>
              <Button
                fullWidth
                variant="outlined"
                onClick={limparFiltros}
                sx={{ height: '46px' }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>

          {/* Total discreto */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total: <b>{formatarMoeda(recebimentos.reduce((sum, r) => sum + (parseFloat(r.valor?.toString()) || 0), 0))}</b> ({recebimentos.length} recebimentos)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 370px)', border: '1px solid #ddd' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data Vencimento</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recebimentos.map((recebimento, index) => (
                    <TableRow 
                      key={recebimento.id}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                      }}
                    >
                      <TableCell>{formatarData(recebimento.data_vencimento)}</TableCell>
                      <TableCell>{clientes.find(c => c.id === recebimento.id_cliente)?.nome || '-'}</TableCell>
                      <TableCell>{recebimento.descricao}</TableCell>
                      <TableCell align="right">{formatarMoeda(recebimento.valor)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={statusOptions.find(opt => opt.value === recebimento.status)?.label}
                          color={statusOptions.find(opt => opt.value === recebimento.status)?.color}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Aba Despesas */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Data Inicial"
                type="date"
                value={filtroDespesas.dataInicio}
                onChange={(e) => setFiltroDespesas(prev => ({ ...prev, dataInicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Data Final"
                type="date"
                value={filtroDespesas.dataFim}
                onChange={(e) => setFiltroDespesas(prev => ({ ...prev, dataFim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" sx={{ height: '46px' }}>
                <InputLabel>Tipo de Despesa</InputLabel>
                <Select
                  value={filtroDespesas.tipo}
                  onChange={(e) => setFiltroDespesas(prev => ({ ...prev, tipo: e.target.value }))}
                  label="Tipo de Despesa"
                  sx={{ height: '46px' }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tiposDespesa.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={carregarDespesas}
                sx={{ height: '46px' }}
                disabled={loading}
              >
                Atualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={limparFiltrosDespesas}
                sx={{ height: '46px' }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>

          {/* Total discreto */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total: <b>{formatarMoeda(despesas.reduce((sum, d) => sum + (parseFloat(d.valor?.toString()) || 0), 0))}</b> ({despesas.length} despesas)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 370px)', border: '1px solid #ddd' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {despesas.map((despesa, index) => (
                    <TableRow 
                      key={despesa.id}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                      }}
                    >
                      <TableCell>{formatarData((despesa.data_pagamento || despesa.data_vencimento || despesa.data_cadastro) || '')}</TableCell>
                      <TableCell>
                        {(() => {
                          const tipoEncontrado = tiposDespesa.find(t => t.id === despesa.id_tipo_despesa);
                          return tipoEncontrado?.nome || '-';
                        })()}
                      </TableCell>
                      <TableCell>{despesa.descricao}</TableCell>
                      <TableCell align="right">{formatarMoeda(despesa.valor)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Aba Raio X */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Data Inicial"
                type="date"
                value={filtroRaioX.dataInicio}
                onChange={(e) => setFiltroRaioX(prev => ({ ...prev, dataInicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Data Final"
                type="date"
                value={filtroRaioX.dataFim}
                onChange={(e) => setFiltroRaioX(prev => ({ ...prev, dataFim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: '46px' } }}
              />
            </Grid>
            <Grid item xs={12} md={3.5}>
              {/* Espaço vazio para equilibrar */}
            </Grid>
            <Grid item xs={12} md={1.75}>
              <Button
                fullWidth
                variant="contained"
                onClick={carregarRaioX}
                sx={{ height: '46px' }}
                disabled={loading}
              >
                Atualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={1.75}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFiltroRaioX({ dataInicio: getPrimeiroDiaDoMes(), dataFim: getUltimoDiaDoMes() })}
                sx={{ height: '46px' }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>

          {/* Total discreto */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total: <b>{formatarMoeda(raioxData.reduce((sum: number, r: any) => sum + ((r.valor_recebimentos || 0) - (r.valor_despesas || 0)), 0))}</b> ({raioxData.length} registros)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 370px)', border: '1px solid #ddd' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell sx={{ paddingRight: '8px' }}>Qtd Recebimentos</TableCell>
                    <TableCell align="right" sx={{ paddingRight: '100px' }}>Valor Recebimentos</TableCell>
                    <TableCell sx={{ paddingRight: '8px' }}>Qtd Despesas</TableCell>
                    <TableCell align="right">Valor Despesas</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {raioxData.map((item, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                      }}
                    >
                      <TableCell>{formatarData(item.data)}</TableCell>
                      <TableCell sx={{ paddingRight: '8px' }}>{item.qtd_recebimentos}</TableCell>
                      <TableCell align="right" sx={{ paddingRight: '100px' }}>{formatarMoeda(item.valor_recebimentos || 0)}</TableCell>
                      <TableCell sx={{ paddingRight: '8px' }}>{item.qtd_despesas}</TableCell>
                      <TableCell align="right">{formatarMoeda(item.valor_despesas || 0)}</TableCell>
                      <TableCell align="right" sx={{ color: (item.valor_recebimentos || 0) - (item.valor_despesas || 0) >= 0 ? 'blue' : 'red' }}>
                        <b>{formatarMoeda((item.valor_recebimentos || 0) - (item.valor_despesas || 0))}</b>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default RaioXFinanceiro;
